import { Box, Typography } from '@mui/material';

import { COLORS } from '../../IndividualPlan/tokens';
import { DateInferredDepartureNote } from '../components/DateInferredDepartureNote';
import { DerivedDepartureNote } from '../components/DerivedDepartureNote';
import { AboutTheseCounts } from '../components/AboutTheseCounts';
import { HeadlineNumbers } from '../components/HeadlineNumbers';
import { Narrative } from '../components/Narrative';
import { TopFindings } from '../components/TopFindings';
import { findSection, reportSettings, type PeContent, type PeReport } from '../types';

const COLUMN_LABEL_SX = {
  m: 0,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: '24px',
  color: COLORS.muted,
  textTransform: 'capitalize',
} as const;

export function ExecutiveSummary({ report, content }: { report: PeReport; content: PeContent }) {
  const narrative = findSection(content, 'executive_summary_narrative');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          id="executive-summary-heading"
          component="h2"
          sx={{ m: 0, fontSize: 24, fontWeight: 600, lineHeight: '32px', color: COLORS.ink }}
        >
          Executive Summary
        </Typography>
        <Typography
          component="p"
          sx={{
            m: 0,
            maxWidth: 720,
            fontSize: 16,
            fontWeight: 500,
            lineHeight: '24px',
            color: COLORS.muted,
          }}
        >
          The year at a glance. Designed to be lifted directly into a board briefing — every claim
          below appears with more detail in the sections that follow.
        </Typography>
      </Box>

      {/* Stacked, not two columns: the eight headline cards run four across, which
          needs the full measure. */}
      <Box
        data-testid="executive-summary-body"
        sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography component="p" sx={COLUMN_LABEL_SX}>
            The Year in a Paragraph
          </Typography>
          <Narrative text={narrative?.text} color={COLORS.ink} />
        </Box>

        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography component="p" sx={COLUMN_LABEL_SX}>
            Headline Numbers
          </Typography>
          <HeadlineNumbers report={report} />
          <DerivedDepartureNote
            count={report.enrollment.departures_this_year?.derived_count ?? 0}
            color={COLORS.muted}
          />
          <DateInferredDepartureNote
            count={report.enrollment.departures_this_year?.date_inferred_count ?? 0}
            color={COLORS.muted}
          />
        </Box>

        <AboutTheseCounts coverage={report.data_coverage} settings={reportSettings(report)} />
      </Box>

      <TopFindings report={report} content={content} />
    </Box>
  );
}
